<template>
  <div class="my-statistics-page">
    <div class="statistics-container">
      <!-- Header -->
      <PageHeader :title="statisticsTitle" icon="uil-chart-line" />

      <!-- Statistics Cards -->
      <StatisticsCards :dashboard-item="dashboard_item" :loading="dashboardLoading" :isGiftButtonActive="isGiftButtonActive"/>

      <!-- LeaderBoard & Point History Row -->
      <div :class="['statistics-row', { 'full-width-history': !isLeaderBoardActive }]">
        <LeaderBoard  v-if="isLeaderBoardActive"
                     :leader-board="leader_board"
                     :loading="leaderboardLoading"
                     :points-count="dashboard_item.points_count"
        />
        <PointHistory :loading="userPointsLoading"
                      :pagination="point_filter.pagination"
                      :user-points="user_points"
                      :window-width="windowWidth"
                      @detail-click="setPointId"
                      @change-page="setPage"
        />
      </div>

      <!-- Pie Charts Row -->
      <div class="statistics-row charts-row">
        <PieChartCard :chart-options="apexEducationOptions"
                      :loading="educationReportLoading"
                      :title="$t('general.types_of_training_assigned_to_me')"
                      :width="chartWidth"
                      :window-width="windowWidth"
                      grid-class="chart-card"
                      icon="uil-chart-pie"
        />
        <PieChartCard :chart-options="apexPointOptions"
                      :loading="pointReportLoading"
                      :title="$t('general.points_i_have_gained_in_the_last_3_months')"
                      :width="chartWidth"
                      :window-width="windowWidth"
                      grid-class="chart-card"
                      icon="uil-calendar-alt"
        />
      </div>

      <AppModal
        :bottom-sheet="true"
        :title="$t('general.point_detail')"
        :visible.sync="showPointDetailModal"
        max-height="90vh"
        width="800px"
      >
        <PointDetailModal :items="user_point_detail" />
      </AppModal>
    </div>
  </div>
</template>

<script>
import Vue from "vue";
import { GET_ITEMS } from "@/core/services/store/REST.module";
import VueApexCharts from 'vue-apexcharts'
import SecureLS from "secure-ls";
import StatisticsCards from "./components/StatisticsCards.vue";
import LeaderBoard from "./components/LeaderBoard.vue";
import PointHistory from "./components/PointHistory.vue";
import PieChartCard from "./components/PieChartCard.vue";
import PointDetailModal from "./components/PointDetailModal.vue";
import PageHeader from "@/view/components/common/PageHeader.vue";
import AppModal from "@/view/components/ui/AppModal.vue";
import widgetMixin from "@/core/mixin/widget.mixin";

const ls = new SecureLS({ encodingType: 'aes', isCompression: true });
Vue.use(VueApexCharts);
Vue.component('apexchart', VueApexCharts)

export default {
  name: "MyStatistics",
  mixins: [widgetMixin],
  components: { StatisticsCards, LeaderBoard, PointHistory, PieChartCard, PointDetailModal, PageHeader, AppModal },
  data() {
    return {
      educationReportData: [],
      pointReportData: [],
      educationReportLoading: true,
      pointReportLoading: true,
      dashboardUrl: 'api/dashboard',
      dashboard_item: {},
      leaderBoardUrl: 'api/dashboard/leader-board',
      leader_board: {},
      leaderboardLoading: true,
      userPointUrl: 'api/user-point',
      userPointsLoading: true,
      dashboardLoading: true,
      user_points: [],
      userLastPointUrl: 'api/last-points',
      userEducationTypeUrl: 'api/user-education-type',
      userPointDetailUrl: 'api/user-point-detail',
      user_point_detail: [],
      point_id: "",
      user_id: "",
      showPointDetailModal: false,
      current_user: [],
      point_filter: {
        pagination: null,
        page: 1,
        perPage: 8,
      },
      menuList: [],
      windowWidth: typeof window !== 'undefined' ? window.innerWidth : 1200,
      resizeTimeout: null
    }
  },
  computed: {
    isGiftButtonActive() {
      return this.isGiftActiveMixin;
    },
    isLeaderBoardActive() {
      try {
        return ls.get('dashboardMenuList')?.dashboard?.find((element) => element.type === 'leader_table')?.status === 1;
      } catch {
        return false;
      }
    },
    statisticsTitle() {
      const sourceMenu = this.getDashboardMenuItems(this.menuList);
      const menuItem = sourceMenu.find(item => item.type === 'my-statistics');
      return this.getLocalizedWidgetTitle(menuItem, this.$t("sideNav.My_statistics"), this.$i18n?.locale);
    },
    chartWidth() {
      const width = this.windowWidth;
      if (width < 576) return Math.max(220, Math.min(340, width - 48));
      if (width < 768) return 380;
      if (width < 992) return 420;
      return 440;
    },
    apexEducationOptions() {
      let chartData = this.educationReportData;

      if (!chartData || !chartData.length) return null;

      let data = [];
      let labels = [];

      chartData.forEach(item => {
        labels.push(item.data);
        data.push(item.count);
      })
      return {
        series: data,
        chartOptions: {
          chart: {
            type: 'pie',
            toolbar: { show: true },
            zoom: { enabled: false },
          },
          colors: ["#ae71ff", "#3e416d", "#2596be", "#36b37e", "#e87a60", "#4572A7", "#AA4643", "#89A54E", "#80699B", "#3D96AE",
            "#DB843D", "#92A8CD", "#A47D7C", "#B5CA92"],
          labels: labels,
        },
      }
    },
    apexPointOptions() {
      let chartData = this.pointReportData;

      if (!chartData || !chartData.length) return null;
      let data = [];
      let labels = [];

      chartData.forEach(item => {
        labels.push(item.data);
        data.push(item.count);
      })
      return {
        series: data,
        chartOptions: {
          chart: {
            type: 'pie',
            toolbar: { show: true },
            zoom: { enabled: false },
          },
          colors: ["#ae71ff", "#3e416d", "#2596be", "#36b37e", "#e87a60", "#4572A7", "#AA4643", "#89A54E", "#80699B", "#3D96AE",
            "#DB843D", "#92A8CD", "#A47D7C", "#B5CA92"],
          labels: labels,
        },
      }
    },
  },
  methods: {
    setPointId(id) {
      if (!id) return
      this.point_id = id
      this.$store.dispatch(GET_ITEMS, {
        url: this.userPointDetailUrl + '/' + id,
        acceptPromise: false,
      }).then(result => {
        if (result.status) {
          this.user_point_detail = result.data
          this.showPointDetailModal = true
        }
      })
    },
    getDashboardData() {
      let self = this;
      self.dashboardLoading = true;
      self.$store.dispatch(GET_ITEMS, {
        url: self.dashboardUrl,
        filters: {
          userId: this.user_id
        },
        acceptPromise: false,
      }).then(result => {
        if (result.status) {
          self.dashboard_item = result.data
        }
        self.dashboardLoading = false;
      })
    },
    getLeaderBoardData() {
      if (!this.isLeaderBoardActive) return false;
      let self = this;
      self.leaderboardLoading = true;

      self.$store.dispatch(GET_ITEMS, {
        url: self.leaderBoardUrl,
        filters: {
          userId: this.user_id
        },
        acceptPromise: false,
      }).then(result => {
        if (result.status) {
          self.leader_board = result.data;
        }
        self.leaderboardLoading = false;
      })
    },
    getUserPoint() {
      let self = this;
      self.userPointsLoading = true;
      self.$store.dispatch(GET_ITEMS, {
        url: self.userPointUrl,
        filters: {
          page: self.point_filter.page,
          perPage: self.point_filter.perPage,
          userId: this.user_id,
        },
        acceptPromise: false,
      }).then(result => {
        if (result.status) {
          self.user_points = result.data.data
          self.point_filter.pagination = result.data
        }
        self.userPointsLoading = false;
      })
    },
    getUserLastPoint() {
      let self = this;
      self.pointReportLoading = true;
      self.$store.dispatch(GET_ITEMS, {
        url: self.userLastPointUrl,
        filters: {
          userId: this.user_id
        },
        acceptPromise: false,
      }).then(result => {
        if (result.status) {
          let items = result.data;
          let results = [];
          items.forEach(subItem => {
            results.push({
              count: Number(subItem.sum),
              data: this.$t(subItem.point_category.key_name),
            })
          })
          self.pointReportData = results;
        }
        self.pointReportLoading = false;
      })
    },
    getUserEducationTypes() {
      let self = this;
      self.educationReportLoading = true;
      self.$store.dispatch(GET_ITEMS, {
        url: self.userEducationTypeUrl,
        filters: {
          userId: this.user_id
        },
        acceptPromise: false,
      }).then(result => {
        if (result.status) {
          let data = result.data;
          let results = [];

          results.push({
            count: Number(data.trainings_count),
            data: self.$t('general.training')
          }, {
            count: Number(data.assignments_count),
            data: self.$t('general.assignments')
          })

          self.educationReportData = results;
        }
        self.educationReportLoading = false;
      })
    },
    setPage(page) {
      let self = this;
      self.point_filter.page = page;
      this.getUserPoint();
    },
    handleResize() {
      if (this.resizeTimeout) {
        clearTimeout(this.resizeTimeout);
      }
      this.resizeTimeout = setTimeout(() => {
        this.windowWidth = window.innerWidth;
      }, 150);
    }
  },
  async mounted() {
    // 1. Widget kontrolü hızlı başlat
    const widgetPromise = this.getWidgetsMixin();

    // 2. Hemen UI hazırla
    this.menuList = this.menuListMixin || [];
    this.user_id = this.$route.params.user;
    this.current_user = JSON.parse(this.secureLocalStorage("user"));

    // 3. Widget kontrolünü bekle
    try {
      await widgetPromise;

      if (!this.isStatisticsActiveMixin) {
        await this.$router.push({ path: "/dashboard" });
        return;
      }
    } catch (error) {
      await this.$router.push({ path: "/dashboard" });
      return;
    }

    // 4. Widget varsa istekleri at
    Promise.all([
      this.getDashboardData(),
      this.getUserPoint(),
      this.getUserLastPoint(),
      this.getLeaderBoardData(),
      this.getUserEducationTypes()
    ]).catch(error => {
      console.error('Veri yükleme hatası:', error);
    });

    window.addEventListener('resize', this.handleResize, { passive: true });
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize);
    if (this.resizeTimeout) {
      clearTimeout(this.resizeTimeout);
    }
  }
}
</script>

<style scoped>
.my-statistics-page {
  width: 100%;
  min-width: 0;
  max-width: 100%;
  box-sizing: border-box;
}

.statistics-container {
  width: 100%;
  min-width: 0;
  max-width: 100%;
  box-sizing: border-box;
}

.statistics-row {
  display: grid;
  grid-template-columns: 2fr 3fr;
  gap: 20px;
  margin-top: 20px;
  min-width: 0;
}

.statistics-row > * {
  min-width: 0;
  max-width: 100%;
}

.full-width-history {
  grid-template-columns: 1fr;
}

.charts-row {
  grid-template-columns: 1fr 1fr;
}

/* Tablet (768px - 1024px) */
@media (max-width: 1024px) {
  .statistics-row {
    grid-template-columns: 1fr 1fr;
    gap: 16px;
  }

  .charts-row {
    grid-template-columns: 1fr 1fr;
  }
}

/* Small Tablet / Large Mobile (576px - 768px) */
@media (max-width: 768px) {
  .statistics-container {
    overflow-x: hidden;
  }

  .statistics-row {
    grid-template-columns: 1fr;
    gap: 14px;
    margin-top: 16px;
  }

  .charts-row {
    grid-template-columns: 1fr;
  }
}

/* Mobile (< 576px) */
@media (max-width: 576px) {
  .statistics-container {
    padding-left: 0;
    padding-right: 0;
  }

  .statistics-row {
    gap: 12px;
    margin-top: 14px;
  }
}
</style>
